import { useCallback, useRef, useState } from 'react'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import { useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { buildUtmUrl, UtmBuilderError, type UtmValidationError } from '@/shared/utm'

const UTILITY = 'utm-generator'
const ANALYTICS_CONTEXT: ToolAnalyticsContext = {
  app: 'devdeck',
  domain: 'devdeck.ru',
  route: '/utm-generator',
  utility: UTILITY,
  processingMode: 'local',
}

function analyticsError(code: UtmValidationError): ToolErrorCode {
  return code === 'unsupported-protocol' ? 'unsupported_input' : 'invalid_input'
}

interface FormValues {
  baseUrl: string
  source: string
  medium: string
  campaign: string
  term: string
  content: string
}

const EMPTY_FORM: FormValues = {
  baseUrl: '',
  source: '',
  medium: '',
  campaign: '',
  term: '',
  content: '',
}

export function UtmGeneratorPage() {
  const t = useT()
  const [form, setForm] = useState<FormValues>(EMPTY_FORM)
  const [result, setResult] = useState('')
  const [errorCode, setErrorCode] = useState<UtmValidationError | null>(null)
  const [copied, setCopied] = useState(false)
  const copyRequestRef = useRef(0)

  const updateField = useCallback((field: keyof FormValues, value: string) => {
    copyRequestRef.current += 1
    setForm((current) => ({ ...current, [field]: value }))
    setResult('')
    setErrorCode(null)
    setCopied(false)
  }, [])

  const handleBuild = useCallback(() => {
    const startedAt = performance.now()
    const analyticsContext = {
      ...ANALYTICS_CONTEXT,
      utmTermPresent: form.term.trim().length > 0,
      utmContentPresent: form.content.trim().length > 0,
    } satisfies ToolAnalyticsContext
    const operation = startToolOperation(analyticsContext)

    try {
      const nextResult = buildUtmUrl(form.baseUrl, form)
      setResult(nextResult)
      copyRequestRef.current += 1
      setErrorCode(null)
      setCopied(false)
      operation.success(performance.now() - startedAt)
    } catch (error) {
      const code = error instanceof UtmBuilderError ? error.code : 'invalid-url'
      setResult('')
      copyRequestRef.current += 1
      setErrorCode(code)
      setCopied(false)
      operation.error(analyticsError(code))
    }
  }, [form])

  const handleCopy = useCallback(async () => {
    if (!result) return
    const request = ++copyRequestRef.current
    try {
      await navigator.clipboard.writeText(result)
      if (request === copyRequestRef.current) setCopied(true)
    } catch {
      if (request === copyRequestRef.current) setCopied(false)
    }
  }, [result])

  const handleReset = useCallback(() => {
    copyRequestRef.current += 1
    setForm(EMPTY_FORM)
    setResult('')
    setErrorCode(null)
    setCopied(false)
  }, [])

  const field = (
    id: keyof FormValues,
    label: string,
    placeholder: string,
    required = false,
  ) => (
    <div className="space-y-2">
      <label htmlFor={`utm-${id}`} className="text-sm font-medium leading-none">
        {label}
      </label>
      <input
        id={`utm-${id}`}
        type={id === 'baseUrl' ? 'url' : 'text'}
        value={form[id]}
        onChange={(event) => updateField(id, event.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className="ym-disable-keys flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  )

  return (
    <ToolPageShell toolKey="utmGenerator">
      <Card className="overflow-hidden shadow-sm">
        <div className="border-b bg-muted/45 px-4 py-3">
          <h2 className="font-mono text-sm font-semibold">{t.utmGenerator.formTitle}</h2>
        </div>
        <CardContent className="space-y-6 p-4 sm:p-6">
          {field('baseUrl', t.utmGenerator.baseUrl, 'https://example.ru/catalog?ref=mail#offer', true)}
          <div className="grid gap-4 md:grid-cols-3">
            {field('source', t.utmGenerator.source, 'newsletter', true)}
            {field('medium', t.utmGenerator.medium, 'email', true)}
            {field('campaign', t.utmGenerator.campaign, 'summer-sale', true)}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {field('term', t.utmGenerator.term, t.utmGenerator.optionalPlaceholder)}
            {field('content', t.utmGenerator.content, t.utmGenerator.optionalPlaceholder)}
          </div>

          {errorCode && (
            <p role="alert" className="rounded-lg border border-destructive/35 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {t.utmGenerator.errors[errorCode]}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleBuild}>{t.utmGenerator.build}</Button>
            <Button variant="outline" onClick={handleReset}>{t.utmGenerator.reset}</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-3">
          <h2 className="font-mono text-sm font-semibold">{t.utmGenerator.previewTitle}</h2>
          <Button variant="outline" size="sm" onClick={handleCopy} className={!result ? 'hidden' : undefined}>
            {copied ? t.utmGenerator.copied : t.utmGenerator.copy}
          </Button>
        </div>
        <CardContent className="p-4 sm:p-6">
          <output
            aria-label={t.utmGenerator.previewLabel}
            className="ym-hide-content block min-h-20 break-all rounded-lg border bg-muted/30 p-4 font-mono text-sm"
          >
            {result || t.utmGenerator.previewPlaceholder}
          </output>
          <p className="mt-3 text-sm text-muted-foreground">{t.utmGenerator.localOnly}</p>
        </CardContent>
      </Card>
    </ToolPageShell>
  )
}
